/**
 * Dynamic Plan Comparison sheet — reads /api/plans/compare and renders a
 * full feature matrix across all visible public plans. Light theme,
 * Indigo/Emerald. Mobile responsive (horizontal scroll on small screens).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Lucide from "lucide-react";
import { Check, X, ArrowRight, Sparkles, Crown, Plus, Minus } from "lucide-react";
import PublicLayout from "@/pages/public/PublicLayout";
import { api } from "@/lib/api";

const CYCLE_LABEL = {
    monthly: "/month",
    annual: "/year",
    one_time: " · one-time",
};

export default function PlanCompare() {
    const [data, setData] = useState(null);
    const [addons, setAddons] = useState([]);
    const [selectedAddons, setSelectedAddons] = useState({}); // {code: qty}
    const [err, setErr] = useState(null);
    const [billing, setBilling] = useState("monthly");

    useEffect(() => {
        api.get("/plans/compare").then(r => setData(r.data)).catch(e => setErr(e?.message || "Could not load plans"));
        api.get("/addons").then(r => setAddons(r.data || [])).catch(() => setAddons([]));
    }, []);

    const toggleAddon = (code, qty) => {
        setSelectedAddons(p => {
            const n = { ...p };
            if (qty <= 0) delete n[code]; else n[code] = qty;
            try { localStorage.setItem("lawos_pending_addons", JSON.stringify(n)); } catch { /* ignore quota */ }
            return n;
        });
    };
    const addonTotal = Object.entries(selectedAddons).reduce((sum, [code, qty]) => {
        const a = addons.find(x => x.code === code);
        return sum + (a ? a.price_inr_per_month * qty : 0);
    }, 0);

    if (err) return <PublicLayout><div className="text-rose-600 text-sm" data-testid="plan-compare-error">{err}</div></PublicLayout>;
    if (!data) return <PublicLayout><div className="text-muted-foreground text-sm py-20 text-center">Loading plans…</div></PublicLayout>;

    // Sort cheapest-first; Enterprise (custom-pricing) always last regardless of price.
    const plans = [...(data.plans || [])].sort((a, b) => {
        const aEnt = a.kind === "enterprise_custom" || a.name === "Enterprise";
        const bEnt = b.kind === "enterprise_custom" || b.name === "Enterprise";
        if (aEnt && !bEnt) return 1;
        if (bEnt && !aEnt) return -1;
        // For non-enterprise: use monthly subscription price; founder/one_time falls back to amortised price (one_time_price_inr / 120 months ≈ 10yr)
        const aP = a.kind === "founder_one_time" || a.kind === "one_time"
            ? (a.one_time_price_inr || 0) / 120
            : (a.price_monthly_inr || 0);
        const bP = b.kind === "founder_one_time" || b.kind === "one_time"
            ? (b.one_time_price_inr || 0) / 120
            : (b.price_monthly_inr || 0);
        return aP - bP;
    });
    const features = data.feature_catalogue || [];
    const matrix = data.matrix || {};

    // Group features for table sections
    const grouped = features.reduce((acc, f) => {
        (acc[f.group] = acc[f.group] || []).push(f);
        return acc;
    }, {});

    const displayPrice = (p) => {
        if (p.kind === "founder_one_time" || p.kind === "one_time") {
            return { price: p.one_time_price_inr, label: " · one-time · 10 years" };
        }
        if (p.kind === "enterprise_custom") {
            return { price: null, label: "Custom" };
        }
        const v = billing === "annual" ? p.price_annual_inr : p.price_monthly_inr;
        return { price: v, label: CYCLE_LABEL[billing] };
    };

    return (
        <PublicLayout>
            <div className="space-y-10" data-testid="plan-compare-page">
                {/* Header */}
                <header className="text-center max-w-3xl mx-auto">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-[11px] font-medium uppercase tracking-[0.18em]">
                        <Sparkles className="w-3 h-3"/> Pricing
                    </span>
                    <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-bold tracking-tight text-foreground">
                        Pick the plan that fits your <span className="text-primary">practice today</span>.
                    </h1>
                    <p className="mt-4 text-muted-foreground leading-relaxed">
                        Every plan includes the wallet for pay-as-you-go AI · WhatsApp · Email · eCourts · Cause List.
                        Pay only the platform fee for software — usage is metered transparently.
                    </p>

                    {/* Billing toggle */}
                    <div className="mt-6 inline-flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border" data-testid="billing-toggle">
                        <button onClick={() => setBilling("monthly")} data-testid="billing-monthly"
                            className={`px-4 py-1.5 text-sm rounded-lg transition-colors ${billing === "monthly" ? "bg-card text-foreground shadow-lawos-sm" : "text-muted-foreground"}`}>
                            Monthly
                        </button>
                        <button onClick={() => setBilling("annual")} data-testid="billing-annual"
                            className={`px-4 py-1.5 text-sm rounded-lg transition-colors inline-flex items-center gap-1.5 ${billing === "annual" ? "bg-card text-foreground shadow-lawos-sm" : "text-muted-foreground"}`}>
                            Annual <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-medium">save 17 %</span>
                        </button>
                    </div>
                </header>

                {/* Plan cards row */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 items-stretch">
                    {plans.map(p => {
                        const { price, label } = displayPrice(p);
                        const featured = p.is_marketing_featured;
                        const founder = p.kind === "founder_one_time";
                        const enterprise = p.kind === "enterprise_custom";
                        return (
                            <div key={p.code} data-testid={`plan-card-${p.code}`}
                                className={`relative bg-card rounded-2xl p-6 border transition-all flex flex-col h-full ${
                                    founder ? "border-primary shadow-lawos-lg ring-2 ring-primary/10" :
                                    featured ? "border-primary shadow-lawos-md" :
                                    "border-border hover:border-primary/40 hover:shadow-lawos-sm"
                                }`}>
                                {founder && (() => {
                                    const total = Number(p.founder_config?.seats_total || 0);
                                    const sold  = Number(p.founder_config?.seats_sold  || 0);
                                    const left  = Math.max(0, total - sold);
                                    const exhausted = left === 0 && total > 0;
                                    return (
                                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-medium flex items-center gap-1 shadow-lawos-sm whitespace-nowrap ${
                                            exhausted
                                                ? "bg-zinc-700 text-white"
                                                : "bg-primary text-primary-foreground"
                                        }`} data-testid="founder-seats-badge">
                                            <Crown className="w-3 h-3"/>
                                            {exhausted ? "Sold out" : `${left} of ${total} seats left`}
                                        </div>
                                    );
                                })()}
                                {featured && !founder && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background dark:bg-primary dark:text-primary-foreground text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full font-medium shadow-lawos-sm">
                                        Most chosen
                                    </div>
                                )}
                                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">{p.name}</div>
                                <div className="mt-3 min-h-[3.5rem]">
                                    {enterprise ? (
                                        <div className="font-heading text-2xl font-bold text-foreground">Custom</div>
                                    ) : Number(price || 0) === 0 && !founder ? (
                                        <div className="flex items-baseline gap-1">
                                            <span className="font-heading text-3xl font-bold text-foreground">Free</span>
                                            <span className="text-xs text-muted-foreground">forever</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-baseline gap-1">
                                            <span className="font-heading text-3xl font-bold text-foreground">₹{Number(price || 0).toLocaleString("en-IN")}</span>
                                            <span className="text-xs text-muted-foreground">{label}</span>
                                        </div>
                                    )}
                                </div>
                                <p className="mt-2 text-xs text-muted-foreground line-clamp-3 min-h-[3rem]">{p.description}</p>
                                <div className="mt-4 space-y-1.5 text-xs flex-1">
                                    <div className="flex justify-between"><span className="text-muted-foreground">Seats</span><span className="text-foreground font-medium">{p.seats_default}{p.extra_seat_inr_per_month > 0 ? <span className="text-muted-foreground"> · +₹{p.extra_seat_inr_per_month}/mo</span> : null}</span></div>
                                    <div className="flex justify-between"><span className="text-muted-foreground">Storage</span><span className="text-foreground font-medium">{p.storage_gb_included} GB</span></div>
                                    {p.quotas && (
                                        <>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">eCourts CNR/mo</span>
                                                <span className="text-foreground font-medium">
                                                    {p.quotas.cnr_sync_per_month === null || p.quotas.cnr_sync_per_month === undefined ? "Unlimited" : p.quotas.cnr_sync_per_month?.toLocaleString("en-IN")}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Cases (lifetime)</span>
                                                <span className="text-foreground font-medium">
                                                    {p.quotas.cases_total === null || p.quotas.cases_total === undefined ? "Unlimited" : p.quotas.cases_total?.toLocaleString("en-IN")}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-muted-foreground">Clients (lifetime)</span>
                                                <span className="text-foreground font-medium">
                                                    {p.quotas.clients_total === null || p.quotas.clients_total === undefined ? "Unlimited" : p.quotas.clients_total?.toLocaleString("en-IN")}
                                                </span>
                                            </div>
                                        </>
                                    )}
                                    {p.accounting?.enabled && (
                                        <div className="flex justify-between"><span className="text-muted-foreground">Txns/mo</span><span className="text-foreground font-medium">{p.accounting.txn_cap_per_month?.toLocaleString("en-IN")}</span></div>
                                    )}
                                    {founder && p.founder_config && (
                                        <div className="flex justify-between"><span className="text-muted-foreground">Seats left</span><span className="text-primary font-semibold">{p.founder_config.seats_total - p.founder_config.seats_sold}/{p.founder_config.seats_total}</span></div>
                                    )}
                                </div>
                                <Link to={enterprise ? "/legal/contact" : `/register?plan=${p.code}`}
                                    data-testid={`plan-cta-${p.code}`}
                                    className={`mt-5 block text-center py-2.5 rounded-lg text-xs font-medium uppercase tracking-[0.14em] transition-all ${
                                        founder || featured
                                            ? "bg-primary text-primary-foreground hover:opacity-90 shadow-lawos-sm"
                                            : "bg-card border border-border hover:border-primary hover:text-primary"
                                    }`}>
                                    {enterprise ? "Talk to sales" : founder ? "Claim Founder seat" : Number(price || 0) === 0 ? "Start free" : "Get started"}
                                </Link>
                            </div>
                        );
                    })}
                </div>

                {/* Feature comparison matrix */}
                <section className="bg-card rounded-2xl border border-border shadow-lawos-sm overflow-hidden">
                    <div className="px-6 py-5 border-b border-border">
                        <h2 className="font-heading text-xl font-semibold text-foreground">Full feature comparison</h2>
                        <p className="text-xs text-muted-foreground mt-1">Every feature, side-by-side. Updated in real time from our plans engine.</p>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="plan-compare-matrix">
                            <thead className="bg-muted/40 text-xs uppercase tracking-[0.14em] text-muted-foreground sticky top-0">
                                <tr>
                                    <th className="text-left px-5 py-3 font-medium min-w-[220px]">Feature</th>
                                    {plans.map(p => (
                                        <th key={p.code} className="px-3 py-3 text-center font-medium min-w-[110px]">{p.name}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {/* Plan price row (anchor) */}
                                <tr className="border-t border-border bg-primary/5">
                                    <td className="px-5 py-3 font-medium text-foreground text-xs uppercase tracking-[0.14em] text-primary">Price</td>
                                    {plans.map(p => {
                                        const { price, label } = displayPrice(p);
                                        const isFree = p.kind !== "enterprise_custom" && Number(price || 0) === 0 && p.kind !== "founder_one_time";
                                        return (
                                            <td key={p.code} className="px-3 py-3 text-center text-xs">
                                                {p.kind === "enterprise_custom" ? <span className="text-foreground font-semibold">Custom</span> :
                                                 isFree ? <span className="text-foreground font-semibold">Free</span> :
                                                 <span><span className="text-foreground font-semibold">₹{Number(price || 0).toLocaleString("en-IN")}</span><span className="text-muted-foreground">{label}</span></span>}
                                            </td>
                                        );
                                    })}
                                </tr>

                                {/* Quantitative rows */}
                                <MatrixRow label="Seats included" plans={plans} render={p => p.seats_default}/>
                                <MatrixRow label="Storage" plans={plans} render={p => `${p.storage_gb_included} GB`}/>
                                <MatrixRow label="Extra seat" plans={plans} render={p => p.extra_seat_inr_per_month > 0 ? `₹${p.extra_seat_inr_per_month}/mo` : "—"}/>
                                {/* Plan quotas — surfaced in the comparison so prospects see the caps */}
                                <MatrixRow label="eCourts CNR / month" plans={plans} render={p => {
                                    const v = p.quotas?.cnr_sync_per_month;
                                    return (v === null || v === undefined) ? "Unlimited" : v.toLocaleString("en-IN");
                                }}/>
                                <MatrixRow label="AI credits / month" plans={plans} render={p => {
                                    const v = p.quotas?.ai_credits_per_month;
                                    if (v === null || v === undefined) return "Unlimited";
                                    return v === 0 ? "—" : v.toLocaleString("en-IN");
                                }}/>
                                <MatrixRow label="WhatsApp msgs / month" plans={plans} render={p => {
                                    const v = p.quotas?.whatsapp_msgs_per_month;
                                    if (v === null || v === undefined) return "Unlimited";
                                    return v === 0 ? "—" : v.toLocaleString("en-IN");
                                }}/>
                                <MatrixRow label="Cases (lifetime)" plans={plans} render={p => {
                                    const v = p.quotas?.cases_total;
                                    return (v === null || v === undefined) ? "Unlimited" : v.toLocaleString("en-IN");
                                }}/>
                                <MatrixRow label="Clients (lifetime)" plans={plans} render={p => {
                                    const v = p.quotas?.clients_total;
                                    return (v === null || v === undefined) ? "Unlimited" : v.toLocaleString("en-IN");
                                }}/>
                                <MatrixRow label="Accounting txns / month" plans={plans} render={p => p.accounting?.enabled ? p.accounting.txn_cap_per_month.toLocaleString("en-IN") : "—"}/>
                                <MatrixRow label="Invoices / month" plans={plans} render={p => p.accounting?.enabled ? p.accounting.invoice_cap_per_month.toLocaleString("en-IN") : "—"}/>
                                <MatrixRow label="GSTIN entities" plans={plans} render={p => p.accounting?.enabled ? p.accounting.gstin_entities : "—"}/>

                                {/* Feature flag rows, grouped */}
                                {Object.entries(grouped).map(([group, items]) => (
                                    <FeatureGroup key={group} group={group} items={items} plans={plans} matrix={matrix}/>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                {/* Add-on selector — replaces the old wallet PAYG block */}
                <section className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-lawos-sm" data-testid="addons-selector">
                    <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                        <div>
                            <h3 className="font-heading text-xl font-semibold text-foreground">Add-ons · pick what you need</h3>
                            <p className="text-sm text-muted-foreground mt-1">Optional monthly extras. Attach during signup or any time from your billing page.</p>
                        </div>
                        {addonTotal > 0 && (
                            <div className="bg-primary/10 border border-primary/20 rounded-xl px-4 py-2.5" data-testid="addons-running-total">
                                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Add-on total</div>
                                <div className="text-lg font-heading font-bold text-primary">₹{addonTotal.toLocaleString("en-IN")}<span className="text-xs text-muted-foreground font-normal">/mo</span></div>
                            </div>
                        )}
                    </div>
                    {addons.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-6">Loading add-ons…</p>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {addons.map(a => (
                                <AddonCard key={a.code} addon={a}
                                    qty={selectedAddons[a.code] || 0}
                                    onChange={qty => toggleAddon(a.code, qty)}/>
                            ))}
                        </div>
                    )}
                    <p className="mt-5 text-[11px] text-muted-foreground italic">All add-ons are charged monthly. Cancel any time from your dashboard — pro-rata refund to wallet.</p>
                </section>

                {/* Founder details (when present) */}
                {plans.find(p => p.kind === "founder_one_time")?.founder_config && (
                    <FounderDetails plan={plans.find(p => p.kind === "founder_one_time")}/>
                )}

                <p className="text-center text-xs text-muted-foreground">
                    All prices in INR. GST extra where applicable. Plans data refreshed in real time from{" "}
                    <code className="bg-muted px-1.5 py-0.5 rounded">/api/plans/compare</code>.
                </p>
            </div>
        </PublicLayout>
    );
}

function MatrixRow({ label, plans, render }) {
    return (
        <tr className="border-t border-border">
            <td className="px-5 py-3 text-foreground/85">{label}</td>
            {plans.map(p => (
                <td key={p.code} className="px-3 py-3 text-center text-sm text-foreground">{render(p)}</td>
            ))}
        </tr>
    );
}

function FeatureGroup({ group, items, plans, matrix }) {
    return (
        <>
            <tr className="border-t border-border bg-muted/30">
                <td colSpan={plans.length + 1} className="px-5 py-2 text-[10px] uppercase tracking-[0.2em] text-primary font-semibold">{group}</td>
            </tr>
            {items.map(f => (
                <tr key={f.key} className="border-t border-border hover:bg-muted/20">
                    <td className="px-5 py-2.5 text-foreground/85 text-sm">{f.label}</td>
                    {plans.map(p => (
                        <td key={p.code} className="px-3 py-2.5 text-center">
                            {matrix[p.code]?.[f.key]
                                ? <Check className="w-4 h-4 text-emerald-600 mx-auto" strokeWidth={2.5}/>
                                : <X className="w-3.5 h-3.5 text-muted-foreground/40 mx-auto" strokeWidth={2}/>}
                        </td>
                    ))}
                </tr>
            ))}
        </>
    );
}

function WalletItem({ label, price }) {
    return (
        <div className="bg-card rounded-lg border border-border px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</div>
            <div className="text-sm font-semibold text-foreground mt-0.5">{price}</div>
        </div>
    );
}

/** Selectable add-on card with quantity stepper. Quantity defaults to 0 (unselected).
 *  Increment per click = addon.increment (e.g. +500 txns/click). */
function AddonCard({ addon, qty, onChange }) {
    const Icon = Lucide[addon.icon] || Lucide.Plus;
    const selected = qty > 0;
    const stepUp   = () => onChange(qty + 1);
    const stepDown = () => onChange(Math.max(0, qty - 1));
    const click    = () => qty === 0 ? onChange(1) : null;
    return (
        <div data-testid={`addon-card-${addon.code}`}
            onClick={click}
            className={`relative rounded-xl border transition-all p-4 ${
                selected ? "border-primary bg-primary/5 shadow-lawos-sm"
                         : "border-border hover:border-primary/40 cursor-pointer"
            }`}>
            {addon.is_recommended && !selected && (
                <span className="absolute -top-2 right-3 bg-emerald-600 text-white text-[9px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full font-medium">Popular</span>
            )}
            <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg shrink-0 flex items-center justify-center ${selected ? "bg-primary text-primary-foreground" : "bg-muted text-primary"}`}>
                    <Icon className="w-4 h-4" strokeWidth={1.75}/>
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground truncate">{addon.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{addon.description}</div>
                </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
                <div className="text-sm">
                    <span className="font-heading text-base font-bold text-foreground">₹{addon.price_inr_per_month}</span>
                    <span className="text-xs text-muted-foreground">/mo</span>
                    {qty > 1 && <span className="text-xs text-primary font-medium ml-2">× {qty} = ₹{(addon.price_inr_per_month * qty).toLocaleString("en-IN")}</span>}
                </div>
                {selected ? (
                    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
                        <button onClick={stepDown} data-testid={`addon-dec-${addon.code}`}
                            className="w-7 h-7 rounded-md border border-border hover:border-primary text-foreground/70 hover:text-primary flex items-center justify-center">
                            <Minus className="w-3.5 h-3.5" strokeWidth={2}/>
                        </button>
                        <span className="text-sm font-medium text-foreground min-w-[20px] text-center">{qty}</span>
                        <button onClick={stepUp} data-testid={`addon-inc-${addon.code}`}
                            className="w-7 h-7 rounded-md bg-primary text-primary-foreground hover:opacity-90 flex items-center justify-center">
                            <Plus className="w-3.5 h-3.5" strokeWidth={2.5}/>
                        </button>
                    </div>
                ) : (
                    <button onClick={(e) => { e.stopPropagation(); onChange(1); }}
                        data-testid={`addon-add-${addon.code}`}
                        className="text-xs uppercase tracking-[0.14em] font-medium text-primary hover:underline">
                        Add +
                    </button>
                )}
            </div>
        </div>
    );
}

function FounderDetails({ plan }) {
    const c = plan.founder_config;
    const left = c.seats_total - c.seats_sold;
    return (
        <section className="rounded-2xl bg-primary text-primary-foreground p-6 sm:p-8 shadow-lawos-lg relative overflow-hidden" data-testid="founder-details">
            <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl"/>
            <div aria-hidden className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-emerald-300/20 blur-3xl"/>
            <div className="relative">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 text-[10px] uppercase tracking-[0.2em] font-semibold">
                    <Crown className="w-3 h-3"/> Founding Member · {left}/{c.seats_total} seats left
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-bold mt-3">₹{plan.one_time_price_inr.toLocaleString("en-IN")} once · {c.access_duration_years} years of Law OS</h3>
                <p className="mt-3 text-primary-foreground/85 text-sm leading-relaxed max-w-2xl">
                    Verified once, yours for {c.access_duration_years} years. Lifetime badge. {c.future_addon_discount_pct}% off all future add-ons.
                    Includes ₹{c.wallet_credit_inr.toLocaleString("en-IN")} wallet credit, {c.wallet_bonus_pct}% bonus top-ups for {c.wallet_bonus_years} years, beta tester role,
                    private founders&apos; Discord and a direct WhatsApp line to the founder for {c.direct_founder_whatsapp_years} years.
                </p>
                <ul className="mt-4 grid sm:grid-cols-2 gap-2 text-sm text-primary-foreground/90 max-w-3xl">
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5"/> {c.seats_primary} primary + {c.seats_juniors} junior + {c.seats_clerks} clerk + {c.seats_accountants} accountant seats</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5"/> Lifetime &ldquo;Founding Member&rdquo; badge on profile &amp; invoices</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5"/> Private founders&apos; Discord + direct WhatsApp line to the founder</li>
                    <li className="flex gap-2"><Check className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5"/> {c.renewal_y10_discount_pct}% off renewal after {c.access_duration_years} years</li>
                </ul>
                <p className="mt-4 text-[11px] text-primary-foreground/70 italic">Founder access is non-transferable and tied to your Bar Council enrollment ID. Manual verification within 48 hrs of signup.</p>
                <Link to={`/register?plan=${plan.code}`} data-testid="founder-cta"
                    className="mt-6 inline-flex items-center gap-2 bg-white text-primary hover:bg-emerald-50 px-7 py-3 rounded-xl font-semibold text-sm shadow-lawos-md transition-colors">
                    Apply for Founder seat <ArrowRight className="w-4 h-4"/>
                </Link>
            </div>
        </section>
    );
}
